import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/authContext';
import { getRecipeCookingSessions, getCookingSessionById } from '../services/api';
import sessionReportService from '../services/sessionReportService';
import { Text, Heading } from './ui';
import { createPortal } from 'react-dom';
import SessionReportViewer from './SessionReportViewer';

const SessionsModal = ({ isOpen, onClose, recipeId }) => {
    const { user } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [sessionReport, setSessionReport] = useState(null);
    const [loadingReport, setLoadingReport] = useState(false);

    const fetchSessions = useCallback(async () => {
        if (!user || !recipeId) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const token = await user.getIdToken();
            console.log('📋 SessionsModal: Fetching sessions for SPECIFIC recipe:', {
                recipeId,
                note: 'Sessions should only be for this exact recipe_id, not the entire lineage'
            });
            const sessionsData = await getRecipeCookingSessions(recipeId, token);
            console.log('📋 SessionsModal: Found sessions:', {
                recipeId,
                sessionCount: sessionsData.length,
                sessionRecipeIds: sessionsData.map(s => s.recipe_id || s.session_recipe_id)
            });
            setSessions(sessionsData);
        } catch (err) {
            console.error('Error fetching sessions:', err);
            setError('Failed to load cooking sessions');
        } finally {
            setLoading(false);
        }
    }, [user, recipeId]);

    useEffect(() => {
        if (isOpen && recipeId && user) {
            fetchSessions();
        }
    }, [isOpen, recipeId, user, fetchSessions]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        if (hours > 0) {
            return `${hours}h ${minutes % 60}m`;
        }
        return `${minutes}m`;
    };

    const handleSessionClick = async (session) => {
        setSelectedSession(session);
        setLoadingReport(true);
        try {
            const token = await user.getIdToken();
            const report = await sessionReportService.getSessionReport(session.id, token);
            setSessionReport(report);
        } catch (error) {
            console.error('Error loading session report:', error);
            setSessionReport(null);
        } finally {
            setLoadingReport(false);
        }
    };

    const handleBackToList = () => {
        setSelectedSession(null);
        setSessionReport(null);
    };

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4" onClick={onClose}>
            <div 
                className="bg-neutral-0 hairline max-w-3xl w-full max-h-[90vh] overflow-hidden relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                >
                    ✕
                </button>
                
                <div className="p-6 overflow-y-auto max-h-[90vh]">
                    <div className="mb-6">
                        <Text className="font-sans text-3xl font-medium text-neutral-900 mb-2">
                            Cooking Sessions
                        </Text>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Text className="text-neutral-900 opacity-60">Loading sessions...</Text>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center py-12">
                            <Text className="text-red-600">{error}</Text>
                        </div>
                    ) : !user ? (
                        <div className="flex items-center justify-center py-12">
                            <Text className="text-neutral-900 opacity-60">Login to view cooking sessions</Text>
                        </div>
                    ) : sessions.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                            <Text className="text-neutral-900 opacity-60">No cooking sessions found</Text>
                        </div>
                    ) : selectedSession ? (
                        <div>
                            <button
                                onClick={handleBackToList}
                                className="mb-4 px-4 py-2 bg-neutral-0 text-neutral-900 hairline font-mono text-sm uppercase tracking-wider hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                            >
                                ← Back to Sessions
                            </button>
                            {loadingReport ? (
                                <div className="flex items-center justify-center py-12">
                                    <Text className="text-neutral-900 opacity-60">Loading session details...</Text>
                                </div>
                            ) : (
                                <SessionReportViewer
                                    sessionId={selectedSession.id}
                                    user={user}
                                    onUpdate={() => {
                                        // Refresh session report if needed
                                        handleSessionClick(selectedSession);
                                    }}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {sessions.map((session, index) => (
                                <div
                                    key={session.id}
                                    className="bg-neutral-50 hairline p-4 cursor-pointer hover:shadow-popup transition-all duration-200"
                                    onClick={() => handleSessionClick(session)}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-center gap-2">
                                            <Text className="font-mono text-sm uppercase tracking-wider">
                                                SESSION {sessions.length - index}
                                            </Text>
                                            {session.was_completed && (
                                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-mono uppercase tracking-wider">
                                                    COMPLETED
                                                </span>
                                            )}
                                        </div>
                                        <Text className="text-xs opacity-60">
                                            {formatDate(session.created_at)}
                                        </Text>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <Text className="text-xs font-mono uppercase tracking-wider opacity-60 mb-1">
                                                DURATION
                                            </Text>
                                            <Text>
                                                {formatDuration(session.actual_duration_seconds)}
                                            </Text>
                                        </div>
                                        <div>
                                            <Text className="text-xs font-mono uppercase tracking-wider opacity-60 mb-1">
                                                STEPS
                                            </Text>
                                            <Text>
                                                {session.total_steps} steps
                                            </Text>
                                        </div>
                                    </div>

                                    {session.reflection_comments && (
                                        <div className="mt-3 pt-3 border-t border-current opacity-20">
                                            <Text className="text-xs font-mono uppercase tracking-wider opacity-60 mb-1">
                                                REFLECTION
                                            </Text>
                                            <Text className="text-sm line-clamp-2">
                                                {session.reflection_comments}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default SessionsModal;

